import React, { useEffect, useState } from "react";
import { Box, Typography, alpha, keyframes, useTheme } from "@mui/material";
import logoText from "../../../assets/logo/text-logo.png";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const BackgroundPattern: React.FC = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        opacity: 0.3,
        zIndex: 0,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(135deg, rgba(0, 11, 24, 0.7) 0%, rgba(0, 30, 60, 0.7) 100%)",
          zIndex: 1,
        },
      }}
    >
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="grid"
            width="60"
            height="60"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 60 0 L 0 0 0 60"
              fill="none"
              stroke="rgba(6, 182, 212, 0.5)"
              strokeWidth="1"
            />
          </pattern>
          <pattern
            id="dots"
            width="30"
            height="30"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="3" cy="3" r="1.5" fill="rgba(6, 182, 212, 0.4)" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
        <rect width="100%" height="100%" fill="url(#dots)" />
      </svg>
    </Box>
  );
};

export const LoadingScreen = ({ isLoading = true }) => {
  const [progress, setProgress] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + Math.random() * 3, 100));
    }, 100);

    return () => clearInterval(interval);
  }, []);

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        overflow: "hidden",
      }}
    >
      <BackgroundPattern />

      {/* Radial Gradient */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "400px",
          height: "400px",
          background:
            "radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0) 70%)",
          borderRadius: "50%",
          filter: "blur(20px)",
          zIndex: 1,
        }}
      />

      {/* Content Container */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          animation: `${fadeIn} 0.6s ease-out`,
          p: 3,
          zIndex: 2,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            width: "180px",
            height: "36px",
            position: "relative",
            mb: 2,
          }}
        >
          <Box
            component="img"
            src={logoText}
            alt="VenFlows"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              filter: "brightness(0.95)",
            }}
          />
        </Box>

        {/* Progress Circle */}
        <Box
          sx={{
            position: "relative",
            width: 100,
            height: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Progress Ring */}
          <svg width="100" height="100" viewBox="0 0 100 100">
            {/* Background Circle */}
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={alpha("#06B6D4", 0.1)}
              strokeWidth="2"
            />
            {/* Progress Circle */}
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="#06B6D4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={`${Math.PI * 90}`}
              strokeDashoffset={`${Math.PI * 90 * (1 - progress / 100)}`}
              transform="rotate(-90 50 50)"
              style={{
                transition: "stroke-dashoffset 0.3s ease",
              }}
            />
          </svg>

          {/* Progress Text */}
          <Typography
            variant="h4"
            sx={{
              position: "absolute",
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "1.25rem",
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Box>

        {/* Status Text */}
        <Box
          sx={{
            textAlign: "center",
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: alpha("#ffffff", 0.7),
              letterSpacing: 1,
              fontSize: "0.875rem",
              textTransform: "uppercase",
            }}
          >
            {progress === 100 ? "Ready" : "Loading"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
